import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import { generatePath } from 'react-router-dom';
import styled from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import Button from '@sorare/core/src/atoms/buttons/Button';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Title6 } from '@sorare/core/src/atoms/typography';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { CardRequirements } from '@sorare/us-sports/src/components/leaderboard/CardRequirements';
import { DivisionLogo } from '@sorare/us-sports/src/components/leaderboard/DivisionLogo';

import { MissingCards_LeaderboardInterface } from './__generated__/index.graphql';

const Wrapper = styled.div`
  background: var(--c-neutral-300);
  border-radius: var(--double-unit);
  padding: var(--double-unit);
  display: flex;
  flex-direction: column;
  gap: var(--double-unit);
  justify-content: flex-start;
  align-items: stretch;
  @media ${tabletAndAbove} {
    flex-direction: row;
    align-items: center;
    & > *:last-child {
      margin-left: auto;
    }
  }
`;

const Inline = styled(Horizontal).attrs({ gap: 2 })``;

type Props = {
  owned?: (Rarity | undefined)[];
  nbCardsRequirement?: 5 | 7;
  competitionDetailBaseLink: string;
  leaderboard: MissingCards_LeaderboardInterface;
};

export const MissingCards = ({
  owned,
  leaderboard,
  nbCardsRequirement = 5,
  competitionDetailBaseLink,
}: Props) => {
  if (!leaderboard || leaderboard.isUserEligible) {
    return null;
  }
  const { slug: leaderboardSlug } = leaderboard;
  return (
    <Wrapper>
      <Inline>
        <DivisionLogo leaderboard={leaderboard} />
        <Vertical>
          <Title6>
            <FormattedMessage
              id="MissingCards.title"
              defaultMessage="Complete your team to be able to play {leaderboard}"
              values={{
                leaderboard: leaderboard.displayName,
              }}
            />
          </Title6>

          <CardRequirements
            nbCards={nbCardsRequirement}
            requirements={leaderboard.requirements}
            owned={owned}
          />
        </Vertical>
      </Inline>
      <Button
        size="medium"
        color="secondary"
        to={generatePath(competitionDetailBaseLink, { leaderboardSlug })}
      >
        <FormattedMessage
          id="MissingCards.competitionDetails"
          defaultMessage="Competition details"
        />
      </Button>
    </Wrapper>
  );
};

MissingCards.fragments = {
  LeaderboardInterface: gql`
    fragment MissingCards_LeaderboardInterface on LeaderboardInterface {
      slug
      displayName
      isUserEligible
      ...DivisionLogo_LeaderboardInterface
      requirements {
        ...CardRequirements_LeaderboardRequirementsInterface
      }
    }
    ${DivisionLogo.fragments.LeaderboardInterface}
    ${CardRequirements.fragments.LeaderboardRequirementsInterface}
  ` as TypedDocumentNode<MissingCards_LeaderboardInterface>,
};
